import {memo, useMemo} from 'react';
import {Stack} from "@mui/material";
import {grey} from "@mui/material/colors";

import {
    buildBookableServiceBookableSessionColor,
    buildBookableServiceLockedSessionColor,
    buildServiceTypeDetails
} from "../../helpers/ServiceTypeUtils";
import {createMultiColorBackground, createRepeatingPattern, isPerformerNotAccreditedForTimeslotWithBookings} from "../../helpers/ShiftHelper";
import {UNBOOKABLE_AND_UNACCREDITED_SERVICE_TYPE_PATTERN} from "../../helpers/ShiftPlanHelper";
import {TIMESLOT_BORDER_COLOR_DARK_GREY} from "./ShiftItemRenderer";
import {TIMERANGE_COLOR_FOR_TRAINING_SESSION, TIMERANGE_COLOR_FOR_UNACCREDITED_PERFORMERS} from "./TimeslotDetailedItemRenderer";
import {SESSION_TYPE} from "../../domain/SessionType";
import '../../css/Schedule.css';

const FULL_SECTION_HEIGHT_PERCENT = 100;

const renderServiceTypeUnbookableStyle = (item) => {
    const serviceTypeDetails = buildServiceTypeDetails(item);
    const hasMatchingSession = item.session?.serviceType && serviceTypeDetails.find(detail => detail.keycode === item.session?.serviceType?.keycode);

    if (item.isBreak || item.sessionType === SESSION_TYPE.TRAINING || hasMatchingSession?.isBookable) {
        return null;
    }

    const serviceTypeUnbookablePattern = UNBOOKABLE_AND_UNACCREDITED_SERVICE_TYPE_PATTERN;

    if (hasMatchingSession) {
        return <Stack
          className={'single-service-type-unbookable'}
          sx={{
              '--backgroundColor': serviceTypeUnbookablePattern
          }}/>
    }

    const serviceTypeBookableStatusList = serviceTypeDetails.map(serviceTypeDetail => serviceTypeDetail.isBookable);
    const sectionHeight = FULL_SECTION_HEIGHT_PERCENT / serviceTypeBookableStatusList.length;

    return serviceTypeBookableStatusList.map((isBookable, index) => {
        if (isBookable) return null;

        return <Stack
          key={index}
          className={'multiple-service-type-unbookable'}
          sx={{
              '--backgroundColor': serviceTypeUnbookablePattern,
              '--index': index,
              '--sectionHeight': sectionHeight
          }}/>
    });
};

function TimeslotBasicItemRenderer({ item, getItemProps }) {

    const isCheckInSession = item.sessionType === SESSION_TYPE.CHECK_IN;

    const timeRangeBackgroundColor = useMemo(() => {

        const serviceTypeDetails = buildServiceTypeDetails(item);
        const existingSessionServiceType = item.session?.serviceType;

        const BLOCKED_SESSION_GREY_COLOR = grey[500];

        const breakSessionColorPattern = `repeating-conic-gradient( ${BLOCKED_SESSION_GREY_COLOR} 0% 25%, ${BLOCKED_SESSION_GREY_COLOR} 0% 50%) 50%/ 0.875rem 0.875rem`;
        const unbookableColorCodePattern = `repeating-conic-gradient( ${BLOCKED_SESSION_GREY_COLOR} 0% 25%, timeRangeColor 0% 50%) 50%/ 0.875rem 0.875rem`;

        const bookableServiceSessionColorMap = serviceTypeDetails.map(serviceType => buildBookableServiceBookableSessionColor(serviceType));
        const existingBookableServiceSessionColor = buildBookableServiceBookableSessionColor(existingSessionServiceType);
        const existingBookableServiceLockedSessionColor = buildBookableServiceLockedSessionColor(existingSessionServiceType);

        const isBreakSession = item.isBreak;
        const isTrainingSession = item.sessionType === SESSION_TYPE.TRAINING;
        const isLockedSession = item.isLocked
        const isUnbookableSession = item.isUnbookable;
        const hasTeachingSession = item.hasSession;
        const hasBookableTeachingSession = hasTeachingSession && item.session?.isBookable;
        const hasUnbookableTeachingSession = hasTeachingSession && !item.session?.isBookable;
        const isSingleServiceTypeUnbookableTeachingSession = hasTeachingSession && isUnbookableSession && serviceTypeDetails.length === 1;
        const isPerformerNotAccredited = isPerformerNotAccreditedForTimeslotWithBookings(item.session?.from, item.session?.to, item.serviceType) && !isCheckInSession;

        const TIMERANGE_COLOR_BOOKABLE_BOOKED = createMultiColorBackground([existingSessionServiceType?.primaryColor]);
        const TIMERANGE_COLOR_BOOKABLE_UNBOOKED = createMultiColorBackground(bookableServiceSessionColorMap);
        const TIMERANGE_COLOR_UNBOOKABLE = createRepeatingPattern(unbookableColorCodePattern, bookableServiceSessionColorMap);
        const TIMERANGE_COLOR_UNBOOKABLE_BREAK = createRepeatingPattern(breakSessionColorPattern, bookableServiceSessionColorMap);
        const TIMERANGE_COLOR_UNBOOKABLE_BOOKED = createRepeatingPattern(unbookableColorCodePattern, [existingSessionServiceType?.primaryColor]);
        const TIMERANGE_COLOR_HAS_SESSION_BOOKABLE = createMultiColorBackground([existingBookableServiceSessionColor]);
        const TIMERANGE_COLOR_HAS_SESSION_UNBOOKABLE = createRepeatingPattern(unbookableColorCodePattern, [existingBookableServiceSessionColor]);
        const TIMERANGE_COLOR_HAS_BOOKED_SESSION_LOCKED = createMultiColorBackground([existingBookableServiceLockedSessionColor]);

        let backgroundColor = TIMERANGE_COLOR_BOOKABLE_UNBOOKED;

        if (isBreakSession) {
            backgroundColor = TIMERANGE_COLOR_UNBOOKABLE_BREAK;
        } else if (isTrainingSession) {
            backgroundColor = TIMERANGE_COLOR_FOR_TRAINING_SESSION;
        } else if (isLockedSession) {
            backgroundColor = TIMERANGE_COLOR_HAS_BOOKED_SESSION_LOCKED;
        } else if (isSingleServiceTypeUnbookableTeachingSession) {
            backgroundColor = TIMERANGE_COLOR_UNBOOKABLE_BOOKED;
        } else if (isUnbookableSession) {
            backgroundColor = TIMERANGE_COLOR_UNBOOKABLE;
        } else if (hasTeachingSession) {
            backgroundColor = TIMERANGE_COLOR_BOOKABLE_BOOKED;
        } else if (hasBookableTeachingSession) {
            backgroundColor = TIMERANGE_COLOR_HAS_SESSION_BOOKABLE;
        } else if (hasUnbookableTeachingSession) {
            backgroundColor = TIMERANGE_COLOR_HAS_SESSION_UNBOOKABLE;
        } else if (isPerformerNotAccredited) {
            return TIMERANGE_COLOR_FOR_UNACCREDITED_PERFORMERS;
        }

        return backgroundColor;
    }, [item, isCheckInSession]);

    const basicItemContainerStyle = useMemo(() => {
        const style = {
            background: timeRangeBackgroundColor || '',
            height: "1.25rem",
            padding: "0.125rem",
            color: item.color,
            border: `0.0625rem solid ${TIMESLOT_BORDER_COLOR_DARK_GREY}`
        };

        if (isCheckInSession) {
            style['border'] = 0;
        }

        return style;

    }, [timeRangeBackgroundColor, item.color, isCheckInSession]);
    
    return (
        <Stack {...getItemProps({ className: "session-container", title: "", style: basicItemContainerStyle })}>{renderServiceTypeUnbookableStyle(item)}</Stack>
    );
}

export default memo(TimeslotBasicItemRenderer);